/**
 * Runs API endpoints — T20.
 * Maps PRD §9.7 to typed fetch calls via apiClient.
 *
 * SSE streaming: use apiClient.openSSE('/api/runs/:id/events', handlers)
 * from the calling component — not wrapped here because SSE returns a handle,
 * not a promise, and components need to manage cleanup via AbortSignal.
 */
import {
  type CreateRunRequest,
  CreateRunRequestSchema,
  ListRunsResponseSchema,
  RunResponseSchema,
} from '@malaberg/shared';
import { apiClient } from '../client';

export interface ListRunsQuery {
  product?: string;
  market?: string;
  status?: string;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
  q?: string;
  limit?: number;
  offset?: number;
  hasWarnings?: boolean;
}

function toQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined);
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

export const runsApi = {
  create(body: CreateRunRequest) {
    return apiClient.request('POST', '/api/runs', {
      body: JSON.stringify(body),
      schema: CreateRunRequestSchema,
    });
  },

  list(query: ListRunsQuery = {}) {
    return apiClient.request(
      'GET',
      `/api/runs${toQueryString(query as Record<string, string | number | boolean | undefined>)}`,
      {
        schema: ListRunsResponseSchema,
      }
    );
  },

  get(id: string) {
    return apiClient.request('GET', `/api/runs/${id}`, {
      schema: RunResponseSchema,
    });
  },

  getMarkdown(id: string): Promise<string> {
    // Returns text/markdown — client returns raw text body
    return apiClient.request<string>('GET', `/api/runs/${id}/markdown`);
  },

  /**
   * Returns the assembled OpenAI prompt for a run. system_owner only —
   * other roles get 403.
   */
  getPrompt(id: string) {
    return apiClient.request<{ ok: true; prompt: string | null; capturedAt: string | null }>(
      'GET',
      `/api/runs/${id}/prompt`
    );
  },

  updateStatus(id: string, status: string) {
    return apiClient.request('PATCH', `/api/runs/${id}`, {
      body: JSON.stringify({ status }),
    });
  },
};
